import {useEffect, useMemo, useState} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {ArrowRight, MoveRight} from "lucide-react";
import {mapApiError} from "../../shared/api/errors";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {moveReferralUser, referralKeys} from "./api";
import {flattenReferralNetwork, validateMoveDraft} from "./contracts";
import {ReferralDialog, ReferralDialogError} from "./ReferralDialog";
import {useTranslation} from "../../shared/i18n";


export const ReferralMoveWorkspace = ({network, refId, countryId, initialUserId = ""}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const users = useMemo(() => flattenReferralNetwork(network), [network]);
    const [userId, setUserId] = useState(() => String(initialUserId || ""));
    const [refType, setRefType] = useState("place");
    const [search, setSearch] = useState("");
    const [preview, setPreview] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [result, setResult] = useState(null);
    const mutation = useMutation({
        mutationFn: () => moveReferralUser({userId, refType, search, countryId}),
        onSuccess: () => {
            setConfirmOpen(false);
            setPreview(null);
            setResult({kind: "success", message: t("sections.referrals.p48")});
            void queryClient.invalidateQueries({queryKey: referralKeys.network(countryId, refId)});
            void queryClient.invalidateQueries({queryKey: referralKeys.all(countryId)});
        },
    });

    useEffect(() => {
        const requested = String(initialUserId || "");
        setUserId(users.some((user) => user.id === requested) ? requested : "");
        setPreview(null);
        setConfirmOpen(false);
        setResult(null);
    }, [initialUserId, users]);

    const selectedUser = users.find((user) => user.id === userId) || null;
    const inspect = () => {
        const validation = validateMoveDraft({userId, refType, search});
        setValidationError(validation.error);
        setResult(null);
        if (!validation.error) setPreview({...validation.values, user: selectedUser});
    };

    return (
        <section className="referral-move-workspace" aria-labelledby="referral-move-title">
            <div className="referral-section-heading">
                <div>
                    <p className="admin-eyebrow">{t("sections.referrals.p49")}</p>
                    <h2 id="referral-move-title">{t("sections.referrals.p50")}</h2>
                </div>
                <MoveRight aria-hidden="true" size={20} />
            </div>
            <div className="referral-move-fields">
                <label className="referral-field">
                    <span>{t("sections.referrals.p51")}</span>
                    <select value={userId} onChange={(event) => { setUserId(event.target.value); setPreview(null); }}>
                        <option value="">{t("sections.referrals.p52")}</option>
                        {users.map((user) => <option key={user.id} value={user.id}>Level {user.level}: {user.name} · {user.phone}</option>)}
                    </select>
                </label>
                <label className="referral-field">
                    <span>{t("sections.referrals.p53")}</span>
                    <select value={refType} onChange={(event) => { setRefType(event.target.value); setSearch(""); setPreview(null); }}>
                        <option value="place">{t("sections.referrals.p54")}</option>
                        <option value="user">{t("sections.referrals.p13")}</option>
                    </select>
                </label>
                <label className="referral-field">
                    <span>{refType === "user" ? t("sections.referrals.p55") : t("sections.referrals.p56")}</span>
                    <input value={search} onChange={(event) => { setSearch(event.target.value); setPreview(null); }} />
                </label>
                <button type="button" className="admin-button admin-button-secondary" onClick={inspect}>{t("sections.referrals.p57")}</button>
            </div>
            {validationError ? <div className="referral-dialog-error" role="alert">{validationError}</div> : null}
            {result ? <MutationResultBanner result={result} /> : null}
            {preview ? (
                <div className="referral-move-preview" role="status" aria-label={t("sections.referrals.p58")}>
                    <div>
                        <span>{t("sections.referrals.p59")}</span>
                        <strong>{preview.user?.name || `#${preview.user_id}`}</strong>
                        <small>{preview.user?.phone}</small>
                    </div>
                    <ArrowRight aria-hidden="true" size={18} />
                    <div>
                        <span>{t("sections.referrals.p60")}</span>
                        <strong>{preview.ref_type === "user" ? t("sections.referrals.p13") : t("sections.referrals.p54")}</strong>
                        <small>{preview.search}</small>
                    </div>
                    <button type="button" className="admin-button admin-button-primary" onClick={() => setConfirmOpen(true)}>{t("sections.referrals.p61")}</button>
                </div>
            ) : null}
            <ReferralDialog open={confirmOpen} title={t("sections.referrals.p62")} eyebrow={preview?.user?.name} pending={mutation.isPending} onClose={() => setConfirmOpen(false)}>
                <div className="referral-dialog-form">
                    <p className="referral-dialog-copy">
                        {t("sections.referrals.p63")}
                    </p>
                    <ReferralDialogError result={mutation.isError ? mapApiError(mutation.error) : null} />
                    <footer className="referral-dialog-footer">
                        <button type="button" className="admin-button admin-button-secondary" disabled={mutation.isPending} onClick={() => setConfirmOpen(false)}>{t("sections.referrals.p7")}</button>
                        <button type="button" className="admin-button admin-button-primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
                            {mutation.isPending ? t("sections.referrals.p59") : t("sections.referrals.p62")}
                        </button>
                    </footer>
                </div>
            </ReferralDialog>
        </section>
    );
};
